/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Stack,
  Grid,
  Typography,
  FormControl,
  FormControlLabel,
  Checkbox,
  IconButton,
  FormHelperText,
  CircularProgress,
  TextField,
  Card,
  CardContent,
  Divider,
  Box,
  Alert,
} from '@mui/material';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from '../components/toast';
import Iconify from '../components/iconify';
import { getUserGroups } from '../utils';
import { LightTooltip } from '../components/custom-components/custom-tooltip';
import { getClients } from '../services/api';
import { newClientContractValidationSchema } from '../utils/newClientContractValidation';
import ClientInfoSection from '../components/contract/ClientInfoSection';
import FeeTypeSection from '../components/contract/FeeTypeSection';
import AdditionalServiceFeesSection from '../components/contract/AdditionalServiceFeesSection';
import ServicesSection from '../components/contract/ServicesSection';
import AdditionalServicesSection from '../components/contract/AdditionalServicesSection';
import PageBreadcrumbs from '../components/common/PageBreadcrumbs';

const FORM_SECTION_COUNT = 4;

function ContractFormSection({ step, title, description, children }) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: 'divider',
        boxShadow: (theme) => theme.customShadows?.z1 || theme.shadows[1],
      }}
    >
      <CardContent sx={{ '&:last-child': { pb: 3 } }}>
        <Stack spacing={3}>
          <Box component="header">
            <Typography
              variant="overline"
              sx={{ fontWeight: 700, letterSpacing: 1, color: 'primary.main', display: 'block' }}
            >
              Step {step} of {FORM_SECTION_COUNT}
            </Typography>
            <Typography variant="h6" component="h2" sx={{ mt: 0.75, fontWeight: 600 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 720, lineHeight: 1.65 }}>
              {description}
            </Typography>
          </Box>
          <Divider />
          <Stack spacing={2}>{children}</Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function NewClientContractPage() {
  const [loading, setLoading] = useState(false);
  const [contractGenerated, setContractGenerated] = useState(false);
  const navigate = useNavigate();
  const [pdfUrl, setPdfUrl] = useState('');
  const [clients, setClients] = useState([]);

  const isAdmin = () => {
    const userGroups = getUserGroups();
    return userGroups.includes('admin');
  };

  // Helper function to fetch all clients across multiple pages
  const fetchAllClients = async () => {
    try {
      let allClients = [];
      let page = 1;
      let hasMore = true;

      // eslint-disable-next-line no-await-in-loop
      while (hasMore) {
        // eslint-disable-next-line no-await-in-loop
        const response = await getClients(page, 100);

        if (response.data.status === 'success') {
          const pageClients = response.data.results || [];
          allClients = [...allClients, ...pageClients];

          // Check if there are more pages
          const { pagination } = response.data;
          hasMore = pagination && pagination.has_next;
          page += 1;
        } else {
          // Handle legacy response format
          if (response.data && Array.isArray(response.data)) {
            return response.data;
          }
          throw new Error(response.data.message || 'Failed to fetch clients');
        }
      }

      return allClients;
    } catch (error) {
      console.error('Error fetching all clients:', error);
      throw error;
    }
  };

  useEffect(() => {
    const userGroups = getUserGroups();
    if (!userGroups.includes('admin') && !userGroups.includes('associate')) {
      navigate('/dashboard', { replace: true });
    } else {
      fetchAllClients()
        .then((allClients) => {
          setClients(allClients);
        })
        .catch(() => {
          const message = 'Cannot get clients, try again later.';
          toast.error(message);
        });
    }
  }, [navigate]);
  const formik = useFormik({
    initialValues: {
      covered_entity: '',
      practice_name: '',
      phone: '',
      address: '',
      fee_type: 'Percentage',
      percentage_terms: 3.5,
      fixed_monthly_fee: '',
      percentage_fee_description: 'of total monthly collections',
      services: [],
      entity_included: false,
      entity_name: '',
      // individuals_included: true,
      // covered_individuals: [],
      activation_date: null,
      include_associate_name: false,
      associate_name: '',
      additional_services: [],
      additional_services_enabled: false,
      additional_service_fees_enabled: false,
      credentialing_per_provider_enabled: false,
      credentialing_per_provider_fee: '',
      prior_authorization_enabled: false,
      prior_authorization_flat_fee: '',
      special_project_rate_enabled: false,
      special_project_rate_per_hour: '',
      other_fee_enabled: false,
      other_service_fee_field_name: '',
      other_service_fee_field_value: '',
    },
    validationSchema: newClientContractValidationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);

        const payload = {
          ...values,
        };

        delete payload.additional_services_enabled;
        delete payload.additional_service_fees_enabled;

        if (values.fee_type === 'Percentage') {
          delete payload.fixed_monthly_fee;
        } else if (values.fee_type === 'Fixed Monthly') {
          delete payload.percentage_terms;
          delete payload.percentage_fee_description;
        }

        if (
          !values.additional_services_enabled ||
          !values.additional_services?.length ||
          !values.additional_services.some((service) => service?.trim())
        ) {
          delete payload.additional_services;
        } else {
          payload.additional_services = values.additional_services
            .filter((service) => service?.trim())
            .map((service) => service.trim());
        }

        delete payload.credentialing_per_provider_enabled;
        delete payload.prior_authorization_enabled;
        delete payload.special_project_rate_enabled;
        delete payload.other_fee_enabled;

        if (!values.credentialing_per_provider_enabled) {
          delete payload.credentialing_per_provider_fee;
        }
        if (!values.prior_authorization_enabled) {
          delete payload.prior_authorization_flat_fee;
        }
        if (!values.special_project_rate_enabled) {
          delete payload.special_project_rate_per_hour;
        }
        if (!values.other_fee_enabled) {
          delete payload.other_service_fee_field_name;
          delete payload.other_service_fee_field_value;
        }

        const token = localStorage.getItem('access');
        const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/contracts/`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const url = response.data.download_url;
        setPdfUrl(url);
        setContractGenerated(true);
        toast.success('PDF successfully created. Select one of the options below.');
        setLoading(false);
      } catch (error) {
        console.error('Error opening PDF:', error);
        toast.error('Error creating contract. Try again later.');
        setLoading(false);
      }
    },
  });
  const handleFieldBlur = (fieldName) => {
    formik.setFieldTouched(fieldName, true, true);
  };

  const handleClientChange = (e) => {
    formik.handleChange(e);
    const selectedClient = clients.find((client) => client.id === e.target.value);
    if (selectedClient) {
      formik.setFieldValue('covered_entity', selectedClient.client_name || selectedClient.practice_name || '');
      formik.setFieldValue('practice_name', selectedClient.practice_name || '');
      formik.setFieldValue('phone', selectedClient.phone.replace(/\D/g, '') || '');
      formik.setFieldValue('address', selectedClient.address || '');
    }
  };

  const handlePDFPrint = () => {
    window.open(pdfUrl, '_blank');
  };

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | New Contract</title>
      </Helmet>

      <Grid container spacing={3}>
        <Grid item xs={12} md={2} lg={3} />
        <Grid item xs={12} md={8} lg={6}>
          <PageBreadcrumbs
            items={[
              { label: 'Admin', href: '/dashboard' },
              { label: 'Agreements', href: '/contracts' },
              { label: 'New Agreement' },
            ]}
          />
          <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 3 }}>
            <LightTooltip title="Return to the agreements list without saving." placement="right">
              <IconButton onClick={() => navigate('/contracts')} aria-label="Back to contracts list" edge="start">
                <ArrowBackIcon />
              </IconButton>
            </LightTooltip>
            <Box sx={{ pt: 0.25 }}>
              <Typography variant="h4" component="h1">
                New contract
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 520, lineHeight: 1.65 }}>
                Complete each step in order. Choosing a client fills in practice details you can still edit before
                generating the PDF.
              </Typography>
            </Box>
          </Stack>

          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={3}>
              {!contractGenerated && (
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                  Required fields are validated when you select <strong>Create contract</strong>. Optional sections stay
                  collapsed until you enable them.
                </Alert>
              )}

              <ContractFormSection
                step={1}
                title="Client & practice"
                description="Select who this agreement is for. Covered entity and practice fields populate from your directory but should match what appears on the contract."
              >
                <ClientInfoSection
                  formik={formik}
                  clients={clients}
                  onClientChange={handleClientChange}
                  onFieldBlur={handleFieldBlur}
                />
              </ContractFormSection>

              <ContractFormSection
                step={2}
                title="Compensation & extra fees"
                description="Define how billing compensation works—percentage or fixed monthly—then add any separate fees (credentialing, prior auth, hourly projects, or custom line items) if they apply."
              >
                <FeeTypeSection formik={formik} onFieldBlur={handleFieldBlur} />
                <AdditionalServiceFeesSection formik={formik} onFieldBlur={handleFieldBlur} />
              </ContractFormSection>

              <ContractFormSection
                step={3}
                title="Services scope"
                description="Check every service line included in this relationship. Use additional services only for items not listed in the standard checklist."
              >
                <ServicesSection formik={formik} onFieldBlur={handleFieldBlur} />
                <AdditionalServicesSection formik={formik} />
              </ContractFormSection>

              <ContractFormSection
                step={4}
                title="Effective date & parties"
                description="Set when coverage begins. Include an associate name only when that party must appear on the generated document."
              >
                <FormControl>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DemoContainer components={['DatePicker']}>
                      <DatePicker
                        id="activation_date"
                        name="activation_date"
                        label="Activation date"
                        value={formik.values.activation_date ? new Date(formik.values.activation_date) : null}
                        onChange={(value) => {
                          if (value && !Number.isNaN(new Date(value).getTime())) {
                            formik.setFieldValue('activation_date', format(value, 'yyyy-MM-dd'));
                          } else {
                            formik.setFieldValue('activation_date', null);
                          }
                        }}
                        onBlur={() => handleFieldBlur('activation_date')}
                        error={formik.touched.activation_date && Boolean(formik.errors.activation_date)}
                        helperText={formik.touched.activation_date && formik.errors.activation_date}
                        sx={{ width: '100%' }}
                      />
                    </DemoContainer>
                  </LocalizationProvider>
                  {formik.touched.activation_date && formik.errors.activation_date && (
                    <FormHelperText error>{formik.errors.activation_date}</FormHelperText>
                  )}
                </FormControl>
                {isAdmin() ? (
                  <FormControl>
                    <LightTooltip title="Include an additional associate on the generated contract document.">
                      <FormControlLabel
                        control={
                          <Checkbox
                            id="include_associate_name"
                            name="include_associate_name"
                            checked={formik.values.include_associate_name}
                            onChange={formik.handleChange}
                            disabled
                          />
                        }
                        label="Include associate name"
                      />
                    </LightTooltip>
                  </FormControl>
                ) : null}
                {formik.values.include_associate_name && (
                  <FormControl>
                    <TextField
                      id="associate_name"
                      name="associate_name"
                      label="Associate name"
                      value={formik.values.associate_name}
                      onChange={formik.handleChange}
                      onBlur={() => handleFieldBlur('associate_name')}
                      error={formik.touched.associate_name && Boolean(formik.errors.associate_name)}
                      helperText={formik.touched.associate_name && formik.errors.associate_name}
                    />
                  </FormControl>
                )}
              </ContractFormSection>

              {!contractGenerated && (
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start" flexWrap="wrap" sx={{ mb: 5 }}>
                  <LightTooltip title="Validate and generate the contract PDF. Fix any highlighted fields if something fails.">
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      sx={{ backgroundColor: '#0A9EF2', px: 3 }}
                      loading={loading}
                      loadingPosition="start"
                      startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                      Create contract
                    </LoadingButton>
                  </LightTooltip>
                  <Button variant="outlined" sx={{ color: '#0A9EF2', borderColor: '#0A9EF2' }} onClick={() => navigate('/contracts')}>
                    Cancel
                  </Button>
                </Stack>
              )}
              {contractGenerated && (
                <Card variant="outlined" sx={{ borderRadius: 2, mb: 5 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Contract ready
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Share, download, or email the PDF, then return to the list when you are finished.
                    </Typography>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
                      <Button variant="contained" startIcon={<Iconify icon="eva:share-fill" />}>
                        Share
                      </Button>
                      <Button variant="contained" onClick={handlePDFPrint} startIcon={<Iconify icon="eva:download-fill" />}>
                        Print/Download
                      </Button>
                      <Button variant="contained" startIcon={<Iconify icon="eva:email-fill" />}>
                        Email
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => {
                          navigate('/contracts', { replace: true });
                        }}
                        startIcon={<Iconify icon="eva:checkmark-square-2-fill" />}
                      >
                        Done
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Stack>
          </form>
        </Grid>
        <Grid item xs={12} md={2} lg={3} />
      </Grid>
    </>
  );
}
